/**
 * ⟦C60 bis · A-177⟧ **Les boutons de la boîte de Matt gagnent sur les zones cliquables de la chambre.**
 *
 * Défaut signalé (03/10/2026) : en 1280 × 800, le bouton « Passer » de la réplique d'accueil de Matt est
 * recouvert par la zone cliquable du bureau (`elementFromPoint` rendait le bureau) : un vrai clic ne
 * l'atteignait pas, et Matt ne pouvait pas être sauté.
 *
 * La cause est une règle ⟦C29 · lot A⟧, validée : les zones cliquables de la chambre (`.objet-cliquable`,
 * `.objet-hotspot-etat`, z-index 9 et 10) passent AU-DESSUS de la boîte de Matt (z-index 8), pour que le
 * survol d'un objet reste possible sous la boîte (« la boîte n'est jamais un voile »). Cette règle ne
 * se défait pas : elle a un prix, ici, c'est qu'un bouton DE la boîte posé sous une zone ne se clique plus.
 *
 * Ce module rend à chaque zone un TROU de la taille exacte des boutons de la boîte qu'elle recouvre
 * (`clip-path` en `evenodd` : un point en dehors du tracé n'est pas une cible de clic). Le reste de la zone
 * (le survol de l'objet sous le corps de la boîte) ne bouge pas, et aucune pile d'empilement n'est touchée.
 *
 * `trouPourControles` est pur (rectangles en entrée, tracé en sortie) : le hook `useControlesDeMattAuDessus`
 * ne fait que mesurer le DOM et poser le tracé.
 */

import { useLayoutEffect, useRef } from "react";

/** Un rectangle en pixels de la fenêtre (`getBoundingClientRect`). */
export interface Rect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/**
 * Le tracé `clip-path` d'une zone cliquable (`zone`) dont on retire les `controles` qui la recouvrent, ou
 * `null` quand aucun ne la touche (la zone est alors laissée telle quelle, sans `clip-path`).
 *
 * Les coordonnées du tracé sont locales à la zone (`path()` se lit dans sa boîte de bordure). Chaque
 * contrôle est rogné à la zone ; un contrôle qui ne la touche pas n'ouvre aucun trou.
 */
export function trouPourControles(zone: Rect, controles: readonly Rect[]): string | null {
  const trous = controles.flatMap((c) => {
    const gauche = Math.max(c.x, zone.x) - zone.x;
    const haut = Math.max(c.y, zone.y) - zone.y;
    const droite = Math.min(c.x + c.width, zone.x + zone.width) - zone.x;
    const bas = Math.min(c.y + c.height, zone.y + zone.height) - zone.y;
    return droite > gauche && bas > haut ? [{ gauche, haut, droite, bas }] : [];
  });
  if (trous.length === 0) return null;
  const arrondi = (n: number) => Math.round(n * 100) / 100;
  const exterieur = `M0 0H${arrondi(zone.width)}V${arrondi(zone.height)}H0Z`;
  const interieurs = trous
    .map((t) => `M${arrondi(t.gauche)} ${arrondi(t.haut)}H${arrondi(t.droite)}V${arrondi(t.bas)}H${arrondi(t.gauche)}Z`)
    .join("");
  return `path(evenodd, "${exterieur}${interieurs}")`;
}

/** Les zones cliquables de la chambre qui passent au-dessus de la boîte de Matt (`app.css`, ⟦C29⟧). */
const ZONES = ".objet-cliquable, .objet-hotspot-etat";
/** Les boutons de la boîte de Matt (« Passer », « Les dessous »…) : tout ce qui s'y clique, sauf le corps. */
const CONTROLES = ".boite-outer button, .boite-outer a[href], .boite-outer [role='button'], .boite-outer input";

/** Pose (ou retire) le trou sur chaque zone, selon l'état actuel du DOM. */
function appliquer(): void {
  const controles = [...document.querySelectorAll<HTMLElement>(CONTROLES)].map((e) => e.getBoundingClientRect());
  for (const zone of document.querySelectorAll<HTMLElement>(ZONES)) {
    // Un bouton DE la boîte n'est pas une zone de la chambre : il ne se troue pas lui-même.
    if (zone.closest(".boite-outer")) continue;
    const trace = controles.length === 0 ? null : trouPourControles(zone.getBoundingClientRect(), controles);
    if (trace === null) {
      if (zone.dataset.trouMatt !== undefined) {
        zone.style.removeProperty("clip-path");
        delete zone.dataset.trouMatt;
      }
    } else if (zone.style.clipPath !== trace) {
      zone.style.setProperty("clip-path", trace);
      zone.dataset.trouMatt = "1";
    }
  }
}

/**
 * Tient les trous à jour tant que la chambre est affichée : à chaque rendu (la boîte apparaît, change de
 * réplique, se ferme), à chaque changement de taille d'une zone, de la boîte ou de la page (la scène se met à
 * l'échelle avec la fenêtre, et les zones se calent après le premier rendu), et à la fin d'une transition ou
 * d'une animation (la boîte qui entre). Sans effet hors navigateur. Les trous sont retirés au démontage.
 */
export function useControlesDeMattAuDessus(): void {
  const observateur = useRef<ResizeObserver | null>(null);
  // Sans tableau de dépendances, volontairement : le DOM de la boîte et des zones change à presque chaque rendu.
  useLayoutEffect(() => {
    if (typeof document === "undefined") return;
    if (observateur.current === null && typeof ResizeObserver === "function") {
      observateur.current = new ResizeObserver(() => appliquer());
    }
    appliquer();
    // `observe` est idempotent : un élément déjà suivi n'est pas suivi deux fois.
    for (const e of document.querySelectorAll(`${ZONES}, .boite-outer, body`)) observateur.current?.observe(e);
  });
  useLayoutEffect(() => {
    if (typeof window === "undefined") return;
    const sur = () => appliquer();
    window.addEventListener("resize", sur);
    document.addEventListener("transitionend", sur, true);
    document.addEventListener("animationend", sur, true);
    return () => {
      window.removeEventListener("resize", sur);
      document.removeEventListener("transitionend", sur, true);
      document.removeEventListener("animationend", sur, true);
      observateur.current?.disconnect();
      observateur.current = null;
      for (const zone of document.querySelectorAll<HTMLElement>("[data-trou-matt]")) {
        zone.style.removeProperty("clip-path");
        delete zone.dataset.trouMatt;
      }
    };
  }, []);
}
